import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Modal,
    Button,
    Select,
    Flex,
    Box,
    Text,
    Stack,
    Loader,
    Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { DateInput, TimeInput } from '@mantine/dates';
import {
    IconAlertTriangle,
    IconCalendarEvent,
    IconClock,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@apollo/client';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import {
    CREATE_STAND_IN,
    UPDATE_STAND_IN,
} from '../../apollo/mutations/standInManagement';
import { useAuth } from '../../context/AuthProvider';
import { GET_DROPDOWN_USERS } from '../../apollo/queries/user';
import { StandIn } from '../../codegen/graphql';
import { GET_STAND_INS } from '../../apollo/queries/standInManagement';

interface Props {
    standIn?: StandIn;
    opened: boolean;
    onClose: () => void;
}

const OPS_DESIGNATIONS = [
    'Divisional Manager',
    'Operations Executive',
    'Senior Operations Executive',
];
const PROCUREMENT_DESIGNATIONS = [
    'Procurement Manager',
    'Senior Procurement Manager',
    'Procurement Executive',
    'Procurement Administrator',
];

function StandInFormModal({ standIn, opened, onClose }: Props) {
    const { authUser } = useAuth();

    const startTimeRef = useRef<HTMLInputElement>();
    const endTimeRef = useRef<HTMLInputElement>();

    const [searchValue, onSearchChange] = useState(
        standIn
            ? `${standIn.substituteUser.firstName} ${standIn.substituteUser.lastName}`
            : '',
    );
    const [userSearchValue] = useDebouncedValue(searchValue, 500);

    const form = useForm({
        initialValues: {
            startDate: standIn?.start ?? dayjs().startOf('day').toDate(),
            startTime: standIn?.start
                ? dayjs(standIn?.start).format('HH:mm')
                : dayjs().startOf('day').format('HH:mm'),
            endDate:
                standIn?.end ?? dayjs().add(7, 'days').endOf('day').toDate(),
            endTime: standIn?.end
                ? dayjs(standIn?.end).format('HH:mm')
                : dayjs().add(7, 'days').endOf('day').format('HH:mm'),
            substituteUserId: standIn?.substituteUser.id ?? '',
        },
    });

    const designationFilter = useMemo(() => {
        if (OPS_DESIGNATIONS.includes(authUser?.designation ?? '')) {
            return OPS_DESIGNATIONS.join(',');
        }

        if (PROCUREMENT_DESIGNATIONS.includes(authUser?.designation ?? '')) {
            return PROCUREMENT_DESIGNATIONS.join(',');
        }

        return undefined;
    }, [authUser]);

    const {
        data: getUsersData,
        loading: fetchingUsers,
        error: getUsersError,
    } = useQuery(GET_DROPDOWN_USERS, {
        variables: {
            nameSearch: userSearchValue,
            designationFilter,
            limit: 20,
        },
    });

    const users = useMemo(
        () =>
            getUsersData?.getUsers.map((user: any) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`,
            })) ?? [],
        [getUsersData],
    );

    useEffect(() => {
        if (getUsersError) {
            console.error(getUsersError);
            notifications.show({
                title: 'Error',
                message: 'Failed to fetch users',
                color: 'red',
            });
        }
    }, [getUsersError]);

    const [
        createStandIn,
        {
            loading: creatingStandIn,
            error: createStandInError,
            reset: resetCreateStandIn,
        },
    ] = useMutation(CREATE_STAND_IN, {
        refetchQueries: [GET_STAND_INS],
    });

    const [
        editStandIn,
        {
            loading: editingStandIn,
            error: editStandInError,
            reset: resetEditStandIn,
        },
    ] = useMutation(UPDATE_STAND_IN, {
        refetchQueries: [GET_STAND_INS],
    });

    const handleSubmit = async (values: typeof form.values) => {
        const [startHours, startMinutes] = values.startTime
            .split(':')
            .map(Number);
        const [endHours, endMinutes] = values.endTime.split(':').map(Number);

        try {
            if (standIn) {
                await editStandIn({
                    variables: {
                        input: {
                            id: standIn.id,
                            primaryUserId: authUser?.id,
                            end: dayjs(values.endDate)
                                .hour(endHours)
                                .minute(endMinutes)
                                .toDate(),
                            start: dayjs(values.startDate)
                                .hour(startHours)
                                .minute(startMinutes)
                                .toDate(),
                            substituteUserId: values.substituteUserId,
                        },
                    },
                });

                notifications.show({
                    title: 'Stand-in Updated',
                    message: 'Stand-in has been successfully updated.',
                    color: 'green',
                });
            } else {
                await createStandIn({
                    variables: {
                        input: {
                            end: dayjs(values.endDate)
                                .hour(endHours)
                                .minute(endMinutes)
                                .toDate(),
                            primaryUserId: authUser?.id,
                            start: dayjs(values.startDate)
                                .hour(startHours)
                                .minute(startMinutes)
                                .toDate(),
                            substituteUserId: values.substituteUserId,
                        },
                    },
                });

                notifications.show({
                    title: 'Stand-in Created',
                    message: 'Stand-in has been successfully created.',
                    color: 'green',
                });
            }
            onClose();
            form.reset();
        } catch (error) {
            console.error(error);
        }
    };

    const modalClose = () => {
        onClose();
        form.reset();
        resetCreateStandIn();
        resetEditStandIn();
    };

    useEffect(() => {
        if (standIn) {
            form.setValues({
                startDate: dayjs(standIn.start).toDate(),
                startTime: dayjs(standIn.start).format('HH:mm'),
                endDate: dayjs(standIn.end).toDate(),
                endTime: dayjs(standIn.end).format('HH:mm'),
                substituteUserId: standIn.substituteUser.id,
            });
            onSearchChange(
                `${standIn.substituteUser.firstName} ${standIn.substituteUser.lastName}`,
            );
        }
    }, [standIn, opened]);

    return (
        <Modal
            opened={opened}
            onClose={modalClose}
            title={standIn ? 'Edit Stand-In' : 'Add Stand-In'}
            size="lg"
            classNames={{
                content: 'bg-[#F8F9FA]',
                header: 'bg-[#F8F9FA]',
                title: 'text-[26px] font-bold',
            }}
        >
            <Box bg="white" p={24} className="rounded-xl">
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap={24}>
                        <Box>
                            <Text className="font-medium" size="16px">
                                Start Date and Time
                            </Text>
                            <Flex gap="xs">
                                <DateInput
                                    leftSection={<IconCalendarEvent />}
                                    label="Date"
                                    minDate={new Date()}
                                    placeholder="Select start date"
                                    w="100%"
                                    size="md"
                                    radius="lg"
                                    variant="default"
                                    classNames={{
                                        label: 'text-sm text-[#ACB5BD] font-light',
                                    }}
                                    popoverProps={{
                                        withinPortal: true,
                                        zIndex: 999,
                                        position: 'top',
                                    }}
                                    {...form.getInputProps('startDate')}
                                />
                                <TimeInput
                                    leftSection={<IconClock />}
                                    label="Time"
                                    placeholder="Select start time"
                                    // @ts-ignore
                                    ref={startTimeRef}
                                    w="100%"
                                    size="md"
                                    radius="lg"
                                    variant="default"
                                    classNames={{
                                        label: 'text-sm text-[#ACB5BD] font-light',
                                    }}
                                    {...form.getInputProps('startTime')}
                                    onFocus={() => {
                                        startTimeRef?.current?.showPicker();
                                    }}
                                />
                            </Flex>
                        </Box>

                        <Box>
                            <Text className="font-medium" size="16px">
                                End Date and Time
                            </Text>
                            <Flex gap="xs">
                                <DateInput
                                    leftSection={<IconCalendarEvent />}
                                    label="Date"
                                    minDate={form.values.startDate}
                                    placeholder="Select end date"
                                    w="100%"
                                    size="md"
                                    radius="lg"
                                    variant="default"
                                    classNames={{
                                        label: 'text-sm text-[#ACB5BD] font-light',
                                    }}
                                    popoverProps={{
                                        withinPortal: true,
                                        zIndex: 999,
                                        position: 'top',
                                    }}
                                    {...form.getInputProps('endDate')}
                                />
                                <TimeInput
                                    leftSection={<IconClock />}
                                    label="Time"
                                    placeholder="Select end time"
                                    // @ts-ignore
                                    ref={endTimeRef}
                                    w="100%"
                                    size="md"
                                    radius="lg"
                                    variant="default"
                                    classNames={{
                                        label: 'text-sm text-[#ACB5BD] font-light',
                                    }}
                                    {...form.getInputProps('endTime')}
                                    onFocus={() => {
                                        endTimeRef?.current?.showPicker();
                                    }}
                                />
                            </Flex>
                        </Box>

                        <Select
                            label="Assigned Stand-in"
                            searchable
                            searchValue={searchValue}
                            onSearchChange={onSearchChange}
                            placeholder="Select user"
                            data={users}
                            rightSection={fetchingUsers && <Loader size="sm" />}
                            {...form.getInputProps('substituteUserId')}
                        />
                        {(createStandInError || editStandInError) && (
                            <Alert
                                className="bg-red-50 text-red-700"
                                classNames={{
                                    message: 'text-[#fa5252]',
                                }}
                                radius="lg"
                                icon={<IconAlertTriangle />}
                            >
                                {createStandInError?.message ?? ''}
                                {editStandInError?.message ?? ''}
                            </Alert>
                        )}
                        <Flex justify="flex-end" gap="xs">
                            <Button
                                variant="outline"
                                fullWidth={false}
                                size="md"
                                onClick={onClose}
                                disabled={creatingStandIn}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                fullWidth={false}
                                size="md"
                                loading={creatingStandIn || editingStandIn}
                            >
                                {standIn ? 'Update' : 'Add'} Stand-In
                            </Button>
                        </Flex>
                    </Stack>
                </form>
            </Box>
        </Modal>
    );
}

export default StandInFormModal;
